import React, { useMemo } from 'react';
import styles from './styles.module.css';

/**
 * TextLinkParser
 *
 * A React component that scans a plain text string for bracketed link syntax
 * of the form `[linkText|href]` and renders the result as inline text interspersed
 * with clickable spans. Each span opens its `href` in a new browser tab when clicked.
 *
 * Props:
 *   - text: string
 *       The source string to parse. Any substrings matching `[text|url]`
 *       will be rendered as clickable link spans; all other text is rendered
 *       as normal inline text.
 *
 * Behavior:
 *   1. Checks for the presence of `[` `|` and `]` to decide whether to parse.
 *   2. Uses a regex (`/\[([^|\]]+)\|([^\]]+)]/g`) to find all `[linkText|href]` tokens.
 *   3. Splits the input into segments, rendering plain text segments as spans
 *      and link segments as clickable spans with `onClick` that calls `window.open`.
 *   4. If `href` is empty or `'#'`, click is a no-op; otherwise it opens `href`
 *      in a new tab (`target="_blank"`).
 *
 * Usage example:
 *   <TextLinkParser text="Need help? [Click here|https://example.com] for details." />
 */

export const TextLinkParser: React.FC<{ text: string }> = ({ text }) => {
  const nodes = useMemo<React.ReactNode[]>(() => {
    const shouldParse =
      text.includes('[') && text.includes('|') && text.includes(']');

    if (!shouldParse) return [text];

    const parts: React.ReactNode[] = [];
    const BRACKET_REGEX = /\[([^|\]]+)\|([^\]]+)]/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = BRACKET_REGEX.exec(text)) !== null) {
      const [fullMatch, linkTextRaw, hrefRaw] = match;
      const index = match.index;

      if (index > lastIndex) {
        parts.push(text.slice(lastIndex, index));
      }

      const linkText = linkTextRaw.trim();
      const href = hrefRaw.trim();

      parts.push(
        <span
          key={`link-${index}`}
          className={styles.button}
          onClick={() => {
            if (!href || href === '#') return;
            window.open(href, '_blank');
          }}
        >
          {linkText}
        </span>
      );

      lastIndex = index + fullMatch.length;
    }

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  }, [text]);

  return <span className={styles.text}>{nodes}</span>;
};
